import axios from 'axios';

import { analyzeFile } from '@/helpers/uploadFileHelper.js';
import { setCampaignsState } from '@/storage/campaignsIndexedDb.js';
import {
  mergeByProperty,
  makeid,
  currentTimestamp,
  currentDateStamp,
  convertDate,
  unpackAderizeMacro
} from './campaigns/helpers.js';
import {
  sharedReporting,
  savedReporting,
  defaultCampaign,
  defaultCreative,
  platformMacros
} from './campaigns/constants.js';

const REQUEST_TIMEOUT = 180000;
axios.defaults.timeout = REQUEST_TIMEOUT;

const API_URL = process.env.VUE_APP_API_URL;
const PREVIEW_ADERSERVE_URL = process.env.VUE_APP_PREVIEW_ADERSERVE_URL;

const CampaignsService = {
  getByID: (store) => (id) => {
    const state = store.campaigns;
    let output = {
      id: id,
      found: false,
      creatives: []
    };
    state.forEach((campaign) => {
      if (campaign.id === id) {
        output = campaign;
        output.found = true;
      }
    });
    return output;
  },
  getCreativeByID: (store) => (id) => {
    const state = store.campaigns;
    let output = null;
    state.forEach((campaign) => {
      if (!campaign.creatives) return;
      campaign.creatives.forEach((creative) => {
        if (creative.id === id) {
          output = creative;
          output.found = true;
        }
      });
    });
    return output;
  },

  retrieveCreative: (store, state) => (id) => {
    return new Promise((resolve, reject) => {
      axios
        .get(API_URL + '/v1/campaign/creative?id=' + id, 
            { withCredentials: true })
        .then(response => {
          if (response.status >= 200 && response.data.status < 400) {
            const creative = response.data.data;
            store.campaigns.forEach((camp) => {
              if (camp.creatives) {
                camp.creatives.forEach((cre) => {
                  if (cre.id === creative.id) {
                    Object.assign(cre, creative);
                    cre.dataPulledAt = currentTimestamp();
                  }
                })
              }
            })
            state.saveState();
            resolve(creative);
          } else {
            reject(response);
          }
        });
    })
  },

  getCampaign: (store, state) => (id) => {
    const campaigns = store.campaigns;
    return new Promise((resolve, reject) => {
      axios
        .get(API_URL + '/v1/campaign/get?campaignID=' + id, { withCredentials: true })
        .then(response => {
          if (response.status >= 200 && response.data.status < 400) {
            const campaign = response.data.data;
            let found = false;
            campaigns.forEach((camp) => {
              if (camp.id === campaign.id) {
                Object.assign(camp, campaign);
                camp.dataPulledAt = currentTimestamp();
                if (!camp.creatives || !(camp.creatives instanceof Array)) camp.creatives = [];
                if (!camp.lineItems || !(camp.lineItems instanceof Array)) camp.lineItems = [];
                found = true;
              }
            })
            if (!found) {
              if (!campaign.creatives || !(campaign.creatives instanceof Array)) campaign.creatives = [];
              if (!campaign.lineItems || !(campaign.lineItems instanceof Array)) campaign.lineItems = [];
              campaigns.push(campaign);
            }
            state.saveState();
            resolve(campaign);
          } else {
            reject(response);
          }
        });
    })
  },

  
  getCampaignDateVideoData: (store, state) => (id, start_dt, end_dt, tzOffset = '+00:00', type = 'json') => {
    return new Promise((resolve, reject) => {
      const camp = state.getByID(id);

      if (!start_dt) {
        start_dt = currentDateStamp() - 60 * 60 * 24 * 7;
      }
      if (!end_dt) {
        end_dt = currentDateStamp(true);
      }

      const startDateTime = start_dt instanceof Date ? start_dt : new Date(start_dt * 1000);
      const endDateTime = end_dt instanceof Date ? end_dt : new Date(end_dt * 1000);

      startDateTime.setUTCHours(0, 0, 0, 0);
      endDateTime.setUTCHours(23, 59, 59);

      /* if (tzOffset && tzOffset != '+00:00') {
        const seconds = tzOffsetToSeconds(tzOffset);
        startDateTime.setTime(startDateTime.getTime() + (seconds * 1000));
        endDateTime.setTime(endDateTime.getTime() + (seconds * 1000));
      } */

      axios
        .get(API_URL + `/v1/campaign/videoData`, {
          withCredentials: true,
          timeout: REQUEST_TIMEOUT,
          params: {
            campaignID: id,
            start_dt: convertDate(startDateTime),
            end_dt: convertDate(endDateTime),
            tzOffset: tzOffset,
            type: type,
            granularity: 'full'
          }
        }
        )
        .then(response => {
          if (response.status >= 200 && response.data.status < 400) {
            const report = response.data.data;
            camp.lastVideoDates = {
              start: start_dt,
              end: end_dt
            };

            state.saveState();
            resolve(report);
          }
        })
        .catch((error) => {
          if (error.code === "ERR_CANCELED") {
            reject("Request timed out");
          } else {
            reject(error.message);
          }
        });
    })
  },

  
  getCampaignDateEvents: (store, state) => (id, start_dt, end_dt, tzOffset = '+00:00', type = 'json') => {
    return new Promise((resolve, reject) => {
      const camp = state.getByID(id);

      if (!start_dt) {
        start_dt = currentDateStamp() - 60 * 60 * 24 * 7;
      }
      if (!end_dt) {
        end_dt = currentDateStamp(true);
      }

      const startDateTime = start_dt instanceof Date ? start_dt : new Date(start_dt * 1000);
      const endDateTime = end_dt instanceof Date ? end_dt : new Date(end_dt * 1000);

      startDateTime.setUTCHours(0, 0, 0, 0);
      endDateTime.setUTCHours(23, 59, 59);

      /* if (tzOffset && tzOffset != '+00:00') {
        const seconds = tzOffsetToSeconds(tzOffset);
        startDateTime.setTime(startDateTime.getTime() + (seconds * 1000));
        endDateTime.setTime(endDateTime.getTime() + (seconds * 1000));
      } */

      axios
        .get(API_URL + `/v1/campaign/events`, {
          withCredentials: true,
          timeout: REQUEST_TIMEOUT,
          params: {
            campaignID: id,
            start_dt: convertDate(startDateTime),
            end_dt: convertDate(endDateTime),
            tzOffset: tzOffset,
            type: type,
            granularity: 'simple'
          }
        }
        )
        .then(response => {
          if (response.status >= 200 && response.data.status < 400) {
            const report = response.data;
            camp.lastEventDates = {
              start: start_dt,
              end: end_dt
            };

            state.saveState();
            resolve(report);
          }
        })
        .catch((error) => {
          if (error.code === "ERR_CANCELED") {
            reject("Request timed out");
          } else {
            reject(error.message);
          }
        });
    })
  },

  getCampaignDateReport: (store, state) => (id, start_dt, end_dt, tzOffset = '+00:00', type = 'json', withVideo = false) => {
    return new Promise((resolve, reject) => {
      const camp = state.getByID(id);
      //console.log("getCampaignDateReport", start_dt, end_dt)
      if (!start_dt) {
        start_dt = currentDateStamp() - 60 * 60 * 24 * 7;
      }
      if (!end_dt) {
        end_dt = currentDateStamp(true);
      }

      const startDateTime = start_dt instanceof Date ? start_dt : new Date(start_dt * 1000);
      const endDateTime = end_dt instanceof Date ? end_dt : new Date(end_dt * 1000);

      startDateTime.setUTCHours(0, 0, 0, 0);
      endDateTime.setUTCHours(23, 59, 59);


      /* if (tzOffset && tzOffset != '+00:00') {
        const seconds = tzOffsetToSeconds(tzOffset);
        startDateTime.setTime(startDateTime.getTime() + (seconds * 1000));
        endDateTime.setTime(endDateTime.getTime() + (seconds * 1000));
      } */

      //let addVideoData = false;

      if (savedReporting[camp.id] && savedReporting[camp.id].lastReportUpdate && type == 'json') {
        if (camp.lastReportDates && camp.lastReportDates.start === start_dt && camp.lastReportDates.end === end_dt && (currentTimestamp() - camp.lastReportUpdate < 3600)) {
          return resolve(savedReporting[camp.id]);
        }
      }/*  else if (savedReporting[camp.id] && type !== 'json') {
        addVideoData = savedReporting[camp.id].videoCompletes > 0;
      } */

      axios
        .get(API_URL + `/v1/campaign/reports`, {
          withCredentials: true,
          timeout: REQUEST_TIMEOUT,
          params: {
            campaignID: id,
            start_dt: convertDate(startDateTime),
            end_dt: convertDate(endDateTime),
            tzOffset: tzOffset,
            type: type,
            granularity: 'full',
            withVideoData: withVideo
          }
        }
        )
        .then(response => {
          if (response.status >= 200 && response.data.status < 400) {
            if (type == 'json') {
              const report = response.data.data;
              savedReporting[camp.id] = report;

              if (report.reportToken && report.reportToken[0]) {
                camp.reportURL = report.reportToken[0].url;
              }
              if (report.scheduledReports) {
                if (!camp.scheduledReports) camp.scheduledReports = [];
                const finalReports = [];
                report.scheduledReports.forEach((incomingSchedule) => {
                  let found = false;
                  camp.scheduledReports.forEach((scheduledReport) => {
                    if (scheduledReport.id === incomingSchedule.id) {
                      found = true;
                      finalReports.push(Object.assign (scheduledReport, incomingSchedule));
                    }
                  })
                  if (!found) {
                    finalReports.push(incomingSchedule);
                  }

                })
                camp.scheduledReports = finalReports;
              }

              camp.lastReportUpdate = currentTimestamp();
              camp.lastReportDates = {
                start: start_dt,
                end: end_dt
              };

              state.saveState();
              resolve(report);
            } else {
              const report = response.data;
              resolve(report);

            }
          }
        })
        .catch((error) => {
          if (error.code === "ERR_CANCELED") {
            reject("Request timed out");
          } else {
            reject(error.message);
          }
        });
    })
  },

  getSharedDateReport: (store, state) => (id, start_dt, end_dt, tzOffset = '+00:00', type = 'json', withVideo = false, liveids = null) => {
    return new Promise((resolve, reject) => {
      if (!start_dt) {
        start_dt = currentDateStamp() - 60 * 60 * 24 * 7;
      }
      if (!end_dt) {
        end_dt = currentDateStamp(true);
      }

      const startDateTime = start_dt instanceof Date ? start_dt : new Date(start_dt * 1000);
      const endDateTime = end_dt instanceof Date ? end_dt : new Date(end_dt * 1000);

      startDateTime.setUTCHours(0, 0, 0, 0);
      endDateTime.setUTCHours(23, 59, 59);

      const liveIdString = liveids && liveids.join(',') || null;

      const cacheKey = id + (liveIdString !== null ? '-'+liveIdString : '');

      /* if (tzOffset && tzOffset != '+00:00') {
        const seconds = tzOffsetToSeconds(tzOffset);
        startDateTime.setTime(startDateTime.getTime() + (seconds * 1000));
        endDateTime.setTime(endDateTime.getTime() + (seconds * 1000));
      } */
      if (sharedReporting[cacheKey] && sharedReporting[cacheKey].lastReportUpdate && type == 'json') {
        if (sharedReporting[cacheKey].lastReportDates && sharedReporting[cacheKey].lastReportDates.start === start_dt && sharedReporting[cacheKey].lastReportDates.end === end_dt && (currentTimestamp() - sharedReporting[cacheKey].lastReportUpdate < 3600)) {
          return resolve(sharedReporting[cacheKey]);
        }
      }

      axios
        .get(API_URL + `/share/reports`, {
          withCredentials: true,
          timeout: REQUEST_TIMEOUT,
          params: {
            sharedToken: id,
            liveIDs: liveIdString,
            start_dt: convertDate(startDateTime),
            end_dt: convertDate(endDateTime),
            tzOffset: tzOffset,
            type: type,
            granularity: 'full',
            withVideoData: withVideo
          }
        }
        )
        .then(response => {
          if (response.status >= 200 && response.data.status < 400) {
            if (type == 'json') {
              const resp = response.data;
              if (resp.data) {
                resp.lastReportUpdate = currentTimestamp();
                resp.lastReportDates = {
                  start: start_dt,
                  end: end_dt
                };
              }
              sharedReporting[cacheKey] = resp;
              state.saveState();
              resolve(resp);
            } else {
              const report = response.data;
              resolve(report);

            }
          }
        })
        .catch((error) => {
          if (error.code === "ERR_CANCELED") {
            reject("Request timed out");
          } else {
            reject(error.message);
          }
        });
    })
  },

  getCreativesDateReport: (store, state) => (id, liveids, start_dt, end_dt, tzOffset = '+00:00', type = 'json', withVideo = false) => {
    return new Promise((resolve, reject) => {
      if (!liveids || liveids.length < 1) reject('No live ids set');
      const camp = state.getByID(id);

      if (!start_dt) {
        start_dt = currentDateStamp() - 60 * 60 * 24 * 7;
      }
      if (!end_dt) {
        end_dt = currentDateStamp(true);
      }

      const startDateTime = start_dt instanceof Date ? start_dt : new Date(start_dt * 1000);
      const endDateTime = end_dt instanceof Date ? end_dt : new Date(end_dt * 1000);

      startDateTime.setUTCHours(0, 0, 0, 0);
      endDateTime.setUTCHours(23, 59, 59);

      /* if (tzOffset && tzOffset != '+00:00') {
        const seconds = tzOffsetToSeconds(tzOffset);
        startDateTime.setTime(startDateTime.getTime() + (seconds * 1000));
        endDateTime.setTime(endDateTime.getTime() + (seconds * 1000));
      } */

      axios
        .get(API_URL + `/v1/campaign/reports`, {
          withCredentials: true,
          timeout: REQUEST_TIMEOUT,
          params: {
            liveIDs: liveids.join(','),
            campaignID: id,
            start_dt: convertDate(startDateTime),
            end_dt: convertDate(endDateTime),
            tzOffset: tzOffset,
            type: type,
            granularity: 'full',
            withVideoData:withVideo
          }
        }
        )
        .then(response => {
          if (response.status >= 200 && response.data.status < 400) {
            const report = response.data.data;
            camp.lastReportDates = {
              start: start_dt,
              end: end_dt
            };

            state.saveState();
            resolve(report);
          }
        })
        .catch((error) => {
          if (error.code === "ERR_CANCELED") {
            reject("Request timed out");
          } else {
            reject(error.message);
          }
        });
    })
  },

  getCreativesDateVideoData: (store, state) => (id, liveids, start_dt, end_dt, tzOffset = '+00:00', type = 'json') => {
    return new Promise((resolve, reject) => {
      //if (!liveids || liveids.length < 1) reject('No live ids set');
      const camp = state.getByID(id);

      if (!start_dt) {
        start_dt = currentDateStamp() - 60 * 60 * 24 * 7;
      }
      if (!end_dt) {
        end_dt = currentDateStamp(true);
      }

      const startDateTime = start_dt instanceof Date ? start_dt : new Date(start_dt * 1000);
      const endDateTime = end_dt instanceof Date ? end_dt : new Date(end_dt * 1000);

      startDateTime.setUTCHours(0, 0, 0, 0);
      endDateTime.setUTCHours(23, 59, 59);

      /* if (tzOffset && tzOffset != '+00:00') {
        const seconds = tzOffsetToSeconds(tzOffset);
        startDateTime.setTime(startDateTime.getTime() + (seconds * 1000));
        endDateTime.setTime(endDateTime.getTime() + (seconds * 1000));
      } */

      const endpoint = id && id.length > 24 ? 'share' : 'v1/campaign';
      
      const params = {
        liveIDs: liveids && liveids.join(','),
        start_dt: convertDate(startDateTime),
        end_dt: convertDate(endDateTime),
        tzOffset: tzOffset,
        type: type,
        granularity: 'full'
      };

      if (id && id.length > 24) {
        params.sharedToken = id;
      }

      axios
        .get(API_URL + `/${endpoint}/videoData`, {
          withCredentials: true,
          timeout: REQUEST_TIMEOUT,
          params: params
        }
        )
        .then(response => {
          if (response.status >= 200 && response.data.status < 400) {
            const report = response.data.data;
            camp.lastEventDates = {
              start: start_dt,
              end: end_dt
            };

            state.saveState();
            resolve(report);
          }
        })
        .catch((error) => {
          if (error.code === "ERR_CANCELED") {
            reject("Request timed out");
          } else {
            reject(error.message);
          }
        });
    })
  },

  getCreativesDateEvents: (store, state) => (id, liveids, start_dt, end_dt, tzOffset = '+00:00', type = 'json') => {
    return new Promise((resolve, reject) => {
      //if (!liveids || liveids.length < 1) reject('No live ids set');
      const camp = state.getByID(id);

      if (!start_dt) {
        start_dt = currentDateStamp() - 60 * 60 * 24 * 7;
      }
      if (!end_dt) {
        end_dt = currentDateStamp(true);
      }

      const startDateTime = start_dt instanceof Date ? start_dt : new Date(start_dt * 1000);
      const endDateTime = end_dt instanceof Date ? end_dt : new Date(end_dt * 1000);

      startDateTime.setUTCHours(0, 0, 0, 0);
      endDateTime.setUTCHours(23, 59, 59);

      /* if (tzOffset && tzOffset != '+00:00') {
        const seconds = tzOffsetToSeconds(tzOffset);
        startDateTime.setTime(startDateTime.getTime() + (seconds * 1000));
        endDateTime.setTime(endDateTime.getTime() + (seconds * 1000));
      } */
      
      const endpoint = id && id.length > 24 ? 'share' : 'v1/campaign';
      
      const params = {
        liveIDs: liveids && liveids.join(','),
        start_dt: convertDate(startDateTime),
        end_dt: convertDate(endDateTime),
        tzOffset: tzOffset,
        type: type,
        granularity: 'simple'
      };

      if (id && id.length > 24) {
        params.sharedToken = id;
      }

      axios
        .get(API_URL + `/${endpoint}/events`, {
          withCredentials: true,
          timeout: REQUEST_TIMEOUT,
          params: params
        }
        )
        .then(response => {
          if (response.status >= 200 && response.data.status < 400) {
            const report = response.data.data;
            camp.lastEventDates = {
              start: start_dt,
              end: end_dt
            };

            state.saveState();
            resolve(report);
          }
        })
        .catch((error) => {
          if (error.code === "ERR_CANCELED") {
            reject("Request timed out");
          } else {
            reject(error.message);
          }
        });
    })
  },

  getCampaignReport: (store, state) => (id, type = 'json', withVideo = false) => {
    return new Promise((resolve, reject) => {
      const camp = state.getByID(id);
      if (camp.report && camp.report.lastUpdated && currentTimestamp() - camp.report.lastUpdated < 3600) {
        const report = camp.report;
        /* if (report.creatives) {
          if (camp.creatives) {
            camp.creatives.forEach((creative) => {
              if (creative.liveCreatives) {
                creative.liveCreatives.forEach((liveCreative) => {
                  if (report.creatives[liveCreative.id]) {
                    liveCreative.reporting = report.creatives[liveCreative.id];
                  }
                })
              }
            })
          }
        } */

        return resolve(report);
      }
      axios
        .get(API_URL + '/v1/campaign/reports?campaignID=' + id + '&type=' + type + '&withVideoData=' + withVideo, { withCredentials: true, timeout: REQUEST_TIMEOUT })
        .then(response => {
          if (response.status >= 200 && response.data.status < 400) {
            const report = response.data.data;
            camp.report = report;
            camp.dataPulledAt = currentTimestamp();

            if (report.reportToken && report.reportToken[0]) {
              camp.reportURL = report.reportToken[0].url;
            }
            if (report.scheduledReports !== null) {
              if (!camp.scheduledReports) camp.scheduledReports = [];

              report.scheduledReports.forEach((incomingSchedule) => {
                let found = false;
                camp.scheduledReports.forEach((scheduledReport) => {
                  if (scheduledReport.id === incomingSchedule.id) {
                    found = true;
                    Object.assign (scheduledReport, incomingSchedule);
                  }
                })
                if (!found) {
                  camp.scheduledReports.push(incomingSchedule);
                }

              })
            }
            /* if (report.creatives) {
              if (camp.creatives) {
                camp.creatives.forEach((creative) => {
                  if (creative.liveCreatives) {
                    creative.liveCreatives.forEach((liveCreative) => {
                      if (report.creatives[liveCreative.id]) {
                        liveCreative.reporting = report.creatives[liveCreative.id];
                      }
                    })
                  }
                })
              }
            } */
            state.saveState();
            resolve(report);
          } else {
            reject(response.data);
          }
        })
        .catch((error) => {
          if (error.code === "ERR_CANCELED") {
            reject("Request timed out");
          } else {
            reject(error.message);
          }
        });
    })
  },

  getCreativesReport: (store, state) => (liveids, type = 'json') => {
    return new Promise((resolve, reject) => {
      axios
        .get(API_URL + '/v1/campaign/reports?liveIDs=' + liveids.join(',') + '&type=' + type, { withCredentials: true, timeout: REQUEST_TIMEOUT })
        .then(response => {
          if (response.status >= 200 && response.data.status < 400) {
            const report = response.data.data;
            /* campaigns.forEach((camp) => {
              if (camp.id === id) {
                Object.assign(camp.data,report);
                camp.dataPulledAt = currentTimestamp();
              }
            }) */
            state.saveState();
            resolve(report);
          } else {
            reject(response.data);
          }
        })
        .catch((error) => {
          if (error.code === "ERR_CANCELED") {
            reject("Request timed out");
          } else {
            reject(error.message);
          }
        });
    })
  },

  getAll: (store, ctx) => (options = {}) => {
    const { reset = false, nextPage = false } = options;
    const PAGE_LIMIT = 50;
    const mineOrAll = store.listView !== 'all';
    const searchTerm = 'search' in options ? (options.search || null) : store.listSearch;

    if (nextPage && !store.listHasMore) {
      return Promise.resolve(store.campaigns);
    }

    if (reset) {
      store.listHasMore = true;
    }

    return new Promise((resolve, reject) => {
      const params = new URLSearchParams();
      params.set('paginate[limit]', String(PAGE_LIMIT));
      params.set('sort[updatedAt]', 'desc');

      const page = reset ? 1 : (store.listPage || 0) + 1;
      params.set('paginate[page]', String(page));

      if (searchTerm) {
        params.set('filter[search]', searchTerm);
      }

      if (reset) {
        params.set('fetchCount', '1');
      }

      axios
        .get(
          API_URL + '/v1/campaign/' + (mineOrAll ? 'mine' : 'all') + '?' + params.toString(),
          { withCredentials: true }
        )
        .then(response => {
          if (response.status >= 200 && response.status < 400 || response.status === 403) {
            if (!response.data || !response.data.data) {
              store.campaigns = [];
              store.listHasMore = false;
              store.listPage = 1;
              if (reset) {
                store.listTotalCount = 0;
                store.listSearch = searchTerm || null;
                ctx.saveState();
              }
              return resolve(store.campaigns);
            }

            const campaigns = response.data.data;
            campaigns.forEach((camp) => {
              camp.dataPulledAt = currentTimestamp();
              if (!camp.creatives || !(camp.creatives instanceof Array)) camp.creatives = [];
            });

            const pagination = response.data.pagination || {};
            store.listPage = pagination.page || page;

            if (reset) {
              store.campaigns = campaigns;
              store.listSearch = searchTerm || null;
              store.listTotalCount = pagination.count != null ? pagination.count : null;
            } else {
              store.campaigns.push(...campaigns);
            }

            store.campaigns.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));

            if (store.listTotalCount != null) {
              store.listHasMore = store.campaigns.length < store.listTotalCount;
            } else {
              store.listHasMore = campaigns.length >= PAGE_LIMIT;
            }

            if (reset) {
              ctx.saveState();
            }
            resolve(store.campaigns);
          } else {
            reject(response.data);
          }
        }, rejected => {
          console.error("Rejected", rejected);
          const response = rejected.response;
          if (!response || !response.data || !response.data.data) {
            if (reset) {
              store.campaigns = [];
              store.listHasMore = false;
            }
            return resolve(store.campaigns);
          }
          reject(rejected);
        });
    });
  },

  getCreativeTags: (state,store) => (creatives, type = 'plain') => {
    const campaign = creatives?.[0]?.campaignID ? store.getByID(creatives[0].campaignID) : null;
    const lineItems = campaign?.lineItems || [];

    const creativeSizeLabel = (creative) => creative.fluid ? 'Responsive' : creative.width + 'x' + creative.height;

    const uniqueSizes = (setCreatives) => {
      const sizes = [];
      setCreatives.forEach((c) => {
        const label = creativeSizeLabel(c);
        if (!sizes.includes(label)) sizes.push(label);
      });
      return sizes;
    };

    const buildTagFromCreative = (creative) => {
      if (!creative?.liveCreatives || creative.liveCreatives.length === 0) return null;

      let tagURL = '';
      let liveID = creative.liveCreatives[0].id;
      if (creative.lineItemID) {
        liveID = creative.lineItemID;
      }
      const platformSpecific = creative.platform && platformMacros[creative.platform] || {};
      if (creative.type === 'VAST' || creative.type === 'VPAID') {
        tagURL = 'https://serve' + (process.env.APP_ENV === 'development' ? '-dev' : '') + '.aderserve.com/v/' + liveID + '/' + liveID + '.xml';
        const euCreative = store.getCreativeSetting(creative, 'euCreative');
        if (euCreative && euCreative === true) {
          tagURL += '?gdpr=${GDPR}&gdpr_consent=${GDPR_CONSENT_1203}&gdpr_pd=0';
        }
        if (platformSpecific.vastSuffix) {
          tagURL += (tagURL.indexOf('?') === -1 ? '?' : '&') + platformSpecific.vastSuffix;
        }
      } else {
        const initURL = 'https://serve' + (process.env.APP_ENV === 'development' ? '-dev' : '') + '.aderserve.com/b/' + liveID + '/init.js';
        let clickURL = '[INSERT CLICK URL]';
        const trackersList = [];

        const extraParameters = [];

        const layoutOptions = store.getCreativeSetting(creative, 'creativeSettings');
        if (layoutOptions && layoutOptions.ad_duration) {
          extraParameters.push('ader-pause-After-Idle="' + layoutOptions.ad_duration + '"');
        }

        if (creative.trackers) {
          if (creative.trackers.click) {
            clickURL = creative.trackers.click[0].url;

            if (creative.platform === 'Quantcast') clickURL = clickURL.replace('servedby.flashtalking.com','[[FLASHTALKING_DOMAIN]]');
          }
          for (let k in creative.trackers) {
            const trackers = creative.trackers[k];
            
            trackers.forEach((tracker, indx) => {
              if (creative.platform === 'Quantcast') tracker.url = tracker.url.replace('servedby.flashtalking.com','[[FLASHTALKING_DOMAIN]]');
              if (k === 'click') {
                if (indx === 0) return;
                const useName = (tracker.name || indx.toString()).replace(/\s+/g,'-');
                trackersList.push('ader-click-tag-' + useName + '' + '="' + tracker.url + '"');
              } else {
                trackersList.push('ader-' + tracker.type + '-tracker' + (trackers.length > 1 ? '[' + indx + ']' : '') + '="' + tracker.url + '"');
              }
            })
          }
        }
        if (platformSpecific.thirdparty) {
          if(trackersList.length > 0) trackersList.push('');
          for (let k in platformSpecific.thirdparty) {
            const tracker = platformSpecific.thirdparty[k];
            trackersList.push('ader-tp-' + k + '="' + tracker + '"');
          }
        }
        if (platformSpecific.additional) {
          if(trackersList.length > 0) trackersList.push('');
          for (let k in platformSpecific.additional) {
            const tracker = platformSpecific.additional[k];
            const prefix = k.indexOf('data-') === 0 ? '' : 'ader-';
            trackersList.push(prefix + k + '="' + tracker + '"');
          }
        }
        if (platformSpecific.conditional) {
          if(trackersList.length > 0) trackersList.push('');
          for (let k in platformSpecific.conditional) {
            if (store.getCreativeSetting(creative, k)) {
              for (let m in platformSpecific.conditional[k]) {
                let tracker = platformSpecific.conditional[k][m];

                if (tracker.indexOf("%%ADERIZE") === 0) {
                  tracker = unpackAderizeMacro(tracker, creative, creatives)
                }
                const prefix = m.indexOf('data-') === 0 ? '' : 'ader-';

                trackersList.push(prefix + m + '="' + tracker + '"');
              }

            }
          }
        }
        const inAppSupport = store.getCreativeSetting(creative, 'inAppSupport');

        tagURL = '<ins ader-id="' + liveID + '"\n\
          ader-clicks="yes"\n\
          ader-does-redirect="yes"\n' +
        (inAppSupport ? 
'          ader-in-app-support="yes"\n' : ''
        ) +
'          ader-click-macro="' + (platformSpecific.clickMacro || '') + '"\n' +
'          ader-click-through="' + clickURL + '"\n\
          ader-width="'+ creative.width + '"\n\
          ader-height="'+ creative.height + '"\n\
          '+ extraParameters.join('\n          ') + '\n\
          '+ trackersList.join('\n          ') + '>\n\
            <script src="' + initURL + '" type="text/javascript" async></script>\n\
</ins>' +
(inAppSupport ? '\n<script src="mraid.js"></script>' : '');
      }
      let tagType = creative.type;
      if (creative.tpTag === true && creative.type === 'VAST') {
        tagType = 'VAST/VPAID';
      }
      const sizeLabel = creativeSizeLabel(creative);
      return {
        creativeId: creative.id,
        name: creative.name,
        tagURL: tagURL,
        sizing: sizeLabel,
        sizes: [sizeLabel],
        creativeIds: [creative.id],
        platform: creative.platform || null,
        type: tagType,
        id: liveID,
        width: creative.width,
        height: creative.height
      };
    };

    const tagOutput = [];
    const processedLineItemIds = new Set();

    (creatives || []).forEach((creative) => {
      if (creative.lineItemID) {
        if (processedLineItemIds.has(creative.lineItemID)) return;
        processedLineItemIds.add(creative.lineItemID);
        const setCreatives = creatives.filter((c) => c.lineItemID === creative.lineItemID);
        const source = setCreatives.find((c) => c.liveCreatives && c.liveCreatives.length > 0);
        if (!source) return;
        const tag = buildTagFromCreative(source);
        if (!tag) return;
        const lineItem = lineItems.find((li) => li.id === creative.lineItemID);
        const sizes = uniqueSizes(setCreatives);
        tag.name = lineItem?.name || source.name;
        tag.sizing = sizes.join(', ');
        tag.sizes = sizes;
        tag.lineItemID = creative.lineItemID;
        tag.creativeIds = setCreatives.map((c) => c.id);
        tagOutput.push(tag);
        return;
      }

      const tag = buildTagFromCreative(creative);
      if (tag) tagOutput.push(tag);
    });

    if (type === 'plain') {
      let tagString = '';
      tagOutput.forEach((tag) => {
        let tagTypeString = tag.tagURL;
        if (tag.type === 'VAST') {
          tagTypeString = 'VAST URL: ' + tag.tagURL;
        } else if (tag.type === 'VPAID') {
          tagTypeString = 'VPAID URL: ' + tag.tagURL;
        }
        tagString += '########## ' + tag.name + ' ##########\n';
        tagString += 'Ad size: ' + tag.sizing + '\n';
        tagString += 'Tag type: ' + tag.type + '\n';
        tagString += tag.platform ? 'Traffic platform: ' + tag.platform + '\n' : '\n';
        tagString += tagTypeString;
        tagString += '\n\n'
      })
      return tagString.trim();
    } else {
      return tagOutput;
    }
  },

  getCreativeSetting: () => (creative, key) => {
    let output = null;
    if (!creative.settings) {
      return output;
    }
    
    creative.settings.forEach((val) => {
      if (val.key === key || (key === 'creativeSettings' && val.key === 'layoutOptions')) {
        output = val.value;
      }
    })
    return output;

  },

  downloadTags: (state,store) => (campaign_id, creatives) => {
    if (!campaign_id || campaign_id === 'new') return;
    //store.loading = true;
    return new Promise((resolve, reject) => {
      const tags = store.getCreativeTags(creatives, 'json');
      const outTags = {};
      tags.forEach((tag) => {
        outTags[tag.id] = tag.tagURL;
      })

      axios
        .post(
          API_URL + '/v1/campaign/tags',
          {
            campaignID: campaign_id,
            type: 'xlsx',
            tags: outTags
          },
          { withCredentials: true }
        )
        .then(response => {
          if (response.status >= 200 && response.data.status < 400) {
            const resp = response.data || [];

            //store.loading = false;
            return resolve(resp);


          }
          return reject(response);
        }).catch((reason) => reject(reason));

    })
  },

  getCreatives: (store, state) => (campaign_id) => {
    if (!campaign_id || campaign_id === 'new') return;
    //store.loading = true;
    return new Promise((resolve, reject) => {
      axios
        .get(
          API_URL + '/v1/campaign/creatives?campaignID=' + campaign_id,
          { withCredentials: true }
        )
        .then(response => {
          if (response.status >= 200 && response.data.status < 400) {
            const creatives = response.data.data || [];
            creatives.forEach((creative) => {
              creative.dataPulledAt = currentTimestamp();
              if (!creative.assets || !(creative.assets instanceof Array)) creative.assets = [];
            })
            //let found = false;
            store.campaigns.forEach((campaign) => {
              if (campaign.id === campaign_id) {
                mergeByProperty(campaign.creatives, creatives, "id");

                state.update(campaign, false);
                //found = true;
              }
            });
            //store.loading = false;
            return resolve(creatives);


          }
          return reject(response);
        }).catch((reason) => reject(reason));

    })
  },

  createNew: (store, state) => (data, onFinish) => {
    if (!data) return false;
    let campaign = Object.assign({}, defaultCampaign, data);
    campaign.updatedAt = currentTimestamp();
    const tempID = 'new_' + makeid(14);
    campaign.id = tempID;
    campaign.new = true;
    campaign.creatives = [];
    axios
      .post(
        API_URL + '/v1/campaign/new',
        campaign,
        { withCredentials: true }
      )
      .then(response => {
        if (response.status === 200) {
          let found = false;
          const responseCampaign = response.data.data;
          if (!responseCampaign.creatives) responseCampaign.creatives = [];
          responseCampaign.dataPulledAt = currentTimestamp();
          store.campaigns.forEach((campaign) => {
            if (campaign.id === tempID || campaign.id === responseCampaign.id) {
              campaign = responseCampaign;
              found = true;
            }
          })
          if (!found) {
            store.campaigns.push(responseCampaign);
          }
          state.saveState();
          if (onFinish) onFinish(responseCampaign);
        }
      });
    return campaign;
  },


  createNewReportToken: () => (data) => {
    return new Promise((resolve, reject) => {
      if (!data) return reject('No data');
      axios
        .post(
          API_URL + '/v1/campaign/newToken',
          data,
          { withCredentials: true }
        )
        .then(response => {
          if (response.status === 200) {
            const responseCampaign = response.data;
            resolve(responseCampaign);
          } else {
            reject(response);
          }
        });
    })

  },


  createNewScheduledReport: (store,state) => (data) => {
    return new Promise((resolve, reject) => {
      if (!data) return reject('No data');
      axios
        .post(
          API_URL + '/v1/campaign/scheduledReport',
          data,
          { withCredentials: true }
        )
        .then(response => {
          if (response.status === 200) {
            const responseCampaign = response.data;
            if (responseCampaign.data) {

              const camp = state.getByID(data.campaignID);
              if (camp) {
                if(!camp.scheduledReports) camp.scheduledReports = [];
                camp.scheduledReports.push(responseCampaign.data)
              }
            }
            resolve(responseCampaign);
          } else {
            reject(response);
          }
        });
    })
  },


  deleteScheduledReport: (store,state) => (id) => {
    return new Promise((resolve, reject) => {
      if (!id) return reject('No data');
      axios
        .delete(
          API_URL + '/v1/campaign/scheduledReport?id='+id,
          { withCredentials: true }
        )
        .then(response => {
          if (response.status === 200) {
            const responseCampaign = response.data.data;
            if (responseCampaign) {
              const camp = state.getByID(responseCampaign.campaignID);
              if (camp) {
                if(!camp.scheduledReports) camp.scheduledReports = [];
                for(let i = 0; i < camp.scheduledReports.length; i++){
                  if(camp.scheduledReports[i].id === responseCampaign.id) {
                    camp.scheduledReports.splice(i,1);
                  }
                }
              }

            }
            resolve(responseCampaign);
          } else {
            reject(response);
          }
        });
    })
  },

  createNewCreative: (store, state) => (campaign_id, data, onFinish) => {
    let campaign = state.getByID(campaign_id);

    if (!data) data = {};
    if (!campaign || campaign.found === false) {
      console.error("Campaign not found", campaign_id)
      state.getCampaign(campaign_id).then((camp) => {
        if (camp) {
          state.createNewCreative(campaign_id, data, onFinish);
        }
      })
      return false;
    }

    if (data.size) {
      const sizeParsed = data.size.split('x');
      data.width = sizeParsed[0];
      data.height = sizeParsed[1];
    }

    let creative = Object.assign({}, defaultCreative, data);
    creative.updatedAt = campaign.updatedAt = currentTimestamp();
    const tempID = 'new_' + makeid(14);
    creative.id = tempID;
    creative.new = true;
    creative.campaignID = campaign_id;
    creative.dataPulledAt = currentTimestamp();

    store.newCreatives[tempID] = null;

    axios
      .post(
        API_URL + '/v1/campaign/creatives',
        creative,
        { withCredentials: true }
      )
      .then(response => {
        if (response.status === 200) {
          let found = false;
          const responseCreative = response.data.data;
          if (!responseCreative.assets) responseCreative.assets = [];
          responseCreative.dataPulledAt = currentTimestamp();
          store.newCreatives[tempID] = responseCreative.id;
          campaign.creatives.forEach((creative, indx) => {
            if (creative.id === tempID) {
              campaign.creatives[indx] = Object.assign(campaign.creatives[indx], responseCreative);
              found = true;
              //store.newCreatives[tempID] = null;
            }
          })
          if (!found) {
            campaign.creatives.push(responseCreative);
          }
          state.update(campaign, false);
          if (onFinish) onFinish(responseCreative);
        }
      });

    return creative;
  },
  updateCreative: (store, state) => (campaign_id, data) => {
    return new Promise((resolve, reject) => {
      let campaign = state.getByID(campaign_id);
      if (!campaign || campaign.found === false) reject('No campaign');

      if (data.id.indexOf('new_') === 0) {
        if (!store.newCreatives[data.id]) {
          console.error('No creative found yet!');
          reject(data);
        }
        data.id = store.newCreatives[data.id];
      }

      data.campaignID = campaign_id;
      axios
        .put(
          API_URL + '/v1/campaign/creatives',
          data,
          { withCredentials: true }
        )
        .then(response => {
          if (response.status === 200) {
            let found = false;
            const responseCreative = response.data.data;

            responseCreative.dataPulledAt = currentTimestamp();
            campaign.creatives.forEach((creative) => {
              if (creative.id === responseCreative.id) {
                creative = Object.assign(creative, responseCreative);
                found = true;
              }
            })
            if (!found) {
              campaign.creatives.push(responseCreative);
            }
            state.update(campaign, false);
            resolve(responseCreative);
          } else {
            reject(response)
          }
        });
    })
  },

  getCreativeHTMLAsset: () => (creative) => {
    const output = (creative.assets || []).filter((asset) => asset.filename.indexOf('.html') >= 0);
    return output.length > 0 ? output[0] : null;
  },
    

  updateCreativeHTML: () => (data) => {
    return new Promise((resolve, reject) => {
      
      axios
        .put(
          API_URL + '/v1/campaign/creatives/html',
          data,
          { withCredentials: true }
        )
        .then(response => {
          if (response.status === 200) {
            const responseCreative = response.data.data;

            responseCreative.dataPulledAt = currentTimestamp();

            resolve(responseCreative);
          } else {
            reject(response)
          }
        });
    })
  },

  getCreativePreviewURL: (store, state) => (campaign_id, creatives) => {
    return new Promise((resolve, reject) => {
      const campaign = state.getByID(campaign_id);
      if (!campaign || campaign.found === false) reject('No campaign');

      const getCreativeSetting = state.getCreativeSetting;
      const tagOutput = state.getCreativeTags(campaign.creatives, 'json');
      const requestedIds = new Set((creatives || []).map((c) => c.id));
      const tagsToPreview = tagOutput.filter((tag) =>
        (tag.creativeIds || [tag.creativeId]).some((id) => requestedIds.has(id))
      );

      const creativesWithTags = (creatives || []).filter((c) =>
        tagsToPreview.some((tag) => (tag.creativeIds || [tag.creativeId]).includes(c.id))
      );

      // If all creatives already have cached preview URLs, return cached data (skip regeneration)
      const allCached = false && creativesWithTags.length > 0 && creativesWithTags.every((c) => getCreativeSetting(c, 'previewUrl'));
      if (allCached) {
        const firstPreviewUrl = getCreativeSetting(creativesWithTags[0], 'previewUrl');
        const campaignPreviewUrl = firstPreviewUrl ? firstPreviewUrl.replace(/\/creative\/[^/]+$/, '') : null;
        const creativesData = tagsToPreview.map((tag) => ({
          id: null,
          creativeId: tag.creativeId,
          lineItemId: tag.lineItemID || undefined,
          previewUrl: getCreativeSetting(creativesWithTags.find((c) => (tag.creativeIds || [tag.creativeId]).includes(c.id)) || creativesWithTags[0], 'previewUrl')
        }));
        const result = { campaignPreviewUrl, creatives: creativesData };
        if (creativesData.length === 1) result.url = creativesData[0].previewUrl;
        return resolve(result);
      }

      // Build request per AD_SERVER_PREVIEW_URL_INTEGRATION.md — one entry per line item or standalone creative
      const previewCreatives = tagsToPreview.map((tag) => {
        const entry = {
          creativeId: tag.creativeId,
          name: tag.name,
          adTag: tag.tagURL,
          width: tag.width || 0,
          height: tag.height || 0,
          sizes: tag.sizes
        };
        if (tag.lineItemID) entry.lineItemId = tag.lineItemID;
        return entry;
      });

      if (previewCreatives.length === 0) {
        return reject(new Error('No creatives with published tags to preview'));
      }

      const data = {
        campaignId: campaign_id,
        name: campaign.name,
        creatives: previewCreatives,
        showDebugPanel: false,
        previewMode: 'newssite'
      };

      axios
        .post(
          PREVIEW_ADERSERVE_URL + '/api/campaigns/ingest',
          data,
          { withCredentials: false }
        )
        .then(async (response) => {
          if (response.status !== 200) return reject(response);

          const responsePreview = response.data.data || response.data;
          const updateCreative = state.updateCreative;

          const applyPreviewUrl = async (creative, previewUrl) => {
            if (!creative || !previewUrl) return;
            const settings = creative.settings || [];
            const existing = settings.find((s) => s.key === 'previewUrl');
            if (existing) {
              existing.value = previewUrl;
              existing.timestamp = currentTimestamp();
            } else {
              settings.push({ key: 'previewUrl', value: previewUrl, timestamp: currentTimestamp() });
            }
            creative.settings = settings;
            await updateCreative(campaign_id, creative);
          };

          const tagForResponse = (respCreative) => tagsToPreview.find((tag) =>
            String(tag.creativeId) === String(respCreative.creativeId) ||
            (respCreative.lineItemId && tag.lineItemID === respCreative.lineItemId) ||
            (tag.creativeIds || []).some((id) => String(id) === String(respCreative.creativeId))
          );

          // Persist preview URL onto the representative creative and all line-item siblings
          if (responsePreview.creatives && updateCreative) {
            for (const respCreative of responsePreview.creatives) {
              if (!respCreative.previewUrl) continue;
              const tag = tagForResponse(respCreative);
              if (!respCreative.lineItemId && tag?.lineItemID) {
                respCreative.lineItemId = tag.lineItemID;
              }
              const siblingIds = tag?.creativeIds || [respCreative.creativeId];
              for (const siblingId of siblingIds) {
                const creative = campaign.creatives.find((c) => c.id === siblingId || c.id === siblingId?.toString());
                await applyPreviewUrl(creative, respCreative.previewUrl);
              }
            }
          }

          if (responsePreview.creatives?.length === 1) {
            responsePreview.url = responsePreview.creatives[0].previewUrl;
          }
          resolve(responsePreview);
        })
        .catch(reject);
    });
  },


  duplicateCreative: (store, state) => (campaign_id, creativeData, keepName = false) => {
    return new Promise((resolve, reject) => {
      let campaign = state.getByID(campaign_id);
      if (!campaign || campaign.found === false) reject('No campaign');

      if (creativeData.id.indexOf('new_') === 0) {
        if (!store.newCreatives[creativeData.id]) {
          console.error('No creative found yet!');
          reject(creativeData);
        }
        creativeData.id = store.newCreatives[creativeData.id];
      }

      creativeData.campaignID = campaign_id;

      const data = Object.assign({},creativeData);
      const copyRegex = /\s+\(Copy\s*(\d*)\)$/g;
      const isACopyName = copyRegex.exec(data.name);
      console.log("Change copy name", isACopyName, data.name);
      if (!keepName) {
        if (!isACopyName) {
          data.name = data.name + ' (Copy)';
        } else {
          let countNumber = 2;
          if (isACopyName[1] != '') {
            countNumber = Number(isACopyName[1]) + 1;
          }
  
          data.name = data.name.replace(copyRegex, ' (Copy ' + countNumber + ')');
        }

      }

      axios
        .put(
          API_URL + '/v1/campaign/creatives/duplicate',
          data,
          { withCredentials: true }
        )
        .then(response => {
          if (response.status === 200) {
            let found = false;
            const responseCreative = response.data.data;

            responseCreative.dataPulledAt = currentTimestamp();
            campaign.creatives.forEach((creative) => {
              if (creative.id === responseCreative.id) {
                creative = Object.assign(creative, responseCreative);
                found = true;
              }
            })
            if (!found) {
              campaign.creatives.push(responseCreative);
            }
            state.update(campaign, false);
            resolve(responseCreative);
          } else {
            reject(response)
          }
        });
    })

  },

  duplicate: (store, state) => (campaignId, name) => {
    return new Promise((resolve, reject) => {
      if (!campaignId) return reject('No campaign id');

      const body = { id: campaignId };
      const trimmedName = typeof name === 'string' ? name.trim() : '';
      if (trimmedName) body.name = trimmedName;

      axios
        .post(
          API_URL + '/v1/campaign/duplicate',
          body,
          { withCredentials: true }
        )
        .then(response => {
          if (response.status >= 200 && response.status < 400) {
            const responseCampaign = response.data.data;
            if (!responseCampaign || !responseCampaign.id) {
              reject(response);
              return;
            }
            if (!responseCampaign.creatives || !(responseCampaign.creatives instanceof Array)) {
              responseCampaign.creatives = [];
            }
            if (!responseCampaign.lineItems || !(responseCampaign.lineItems instanceof Array)) {
              responseCampaign.lineItems = [];
            }
            responseCampaign.dataPulledAt = currentTimestamp();

            let found = false;
            store.campaigns.forEach((campaign) => {
              if (campaign.id === responseCampaign.id) {
                Object.assign(campaign, responseCampaign);
                found = true;
              }
            });
            if (!found) {
              store.campaigns.push(responseCampaign);
            }
            state.saveState();
            resolve(responseCampaign);
          } else {
            reject(response);
          }
        })
        .catch(reject);
    });
  },

  publishCreative: (store, state) => (creative) => {
    return new Promise((resolve, reject) => {
      axios
        .post(
          API_URL + '/v1/campaign/creatives/publish',
          creative,
          { withCredentials: true }
        )
        .then(response => {
          if (response.status === 200) {
            const responseCreative = response.data.data;

            responseCreative.dataPulledAt = currentTimestamp();
            creative = responseCreative;
            let campaign = state.getByID(creative.campaignID);
            if (campaign) {
              campaign.updatedAt = currentTimestamp();
              let found = false;
              campaign.creatives.forEach((creative) => {
                if (creative.id === responseCreative.id) {
                  creative = Object.assign(creative, responseCreative);
                  found = true;
                }
              })
              if (!found) {
                campaign.creatives.push(responseCreative);
              }
              state.update(campaign, false);

            }
            resolve(responseCreative);
          } else {
            reject(response);
          }
        }).catch((e) => {
          reject(e);
        });
    })

  },

  optimizeAssets: () => (creative) => {
    return new Promise((resolve, reject) => {
      axios
        .post(
          API_URL + '/v1/campaign/creatives/optimizeAssets',
          {creativeID: creative.id},
          { withCredentials: true }
        )
        .then(response => {
          if (response.status === 200) {
            const responseAssets = response.data.data;
            if(responseAssets) {
              creative.assets = responseAssets;
            }
            resolve(creative);
          } else {
            reject(response);
          }
        }).catch((e) => {
          reject(e);
        });
    })

  },


  deleteCreative: (store, state) => (creative) => {
    return new Promise((resolve, reject) => {
      axios
        .delete(
          API_URL + '/v1/campaign/creatives/delete?id=' + creative.id,
          { withCredentials: true }
        )
        .then(response => {
          if (response.status === 200) {
            const responseCreative = response.data.data;

            let campaign = state.getByID(creative.campaignID);
            if (campaign) {
              campaign.updatedAt = currentTimestamp();
              campaign.creatives.forEach((creative, indx) => {
                if (creative.id === responseCreative.id) {
                  campaign.creatives.splice(indx, 1);
                }
              })
              state.update(campaign, false);
            }
            resolve(responseCreative);
          } else {
            reject(response);
          }
        });
    })

  },

  update: (store, state) => (data, updateBackend = true) => {
    if (!data) return false;
    let campaign = null;
    for (let i = store.campaigns.length - 1; i >= 0; --i) {
      if (store.campaigns[i].id == data.id) {
        campaign = Object.assign(store.campaigns[i], data);
      }
    }
    if (updateBackend) {
      axios
        .put(
          API_URL + '/v1/campaign/update',
          campaign,
          { withCredentials: true }
        )
        .then(response => {
          if (response.status === 200) {
            const responseCamp = response.data.data;

            responseCamp.dataPulledAt = currentTimestamp();
            for (let i = store.campaigns.length - 1; i >= 0; --i) {
              if (store.campaigns[i].id == data.id) {
                if (store.campaigns[i].creatives) delete responseCamp.creatives;
                campaign = Object.assign(store.campaigns[i], responseCamp);
              }
            }
            state.saveState();
          } else {
            console.error(response);
          }
        });
    }

    state.saveState();
    return campaign;
  },

  uploadAsset: (store, state) => async (campaignID, creativeID, file, hasTriedBefore = false) => {

    return new Promise((resolve, reject) => {
      if (!campaignID || !creativeID) {
        return reject('Incorrect campaign or creative!');
      }
      let campaign = state.getByID(campaignID);
      if (!campaign || campaign.found === false) {
        console.error("CAMPAIGN NOT FOUND")
        if (hasTriedBefore !== true) {
          state.getCampaign(campaignID).then((camp) => {
            if (camp) {
              campaign = camp;
              state.uploadAsset(campaignID, creativeID, file, true).then(val => resolve(val)).catch(val => reject(val));
            }
          })
        } else {
          return reject('Campaign not found!')
        }
        return false;
      }
      let creative;
      for (let i = campaign.creatives.length - 1; i >= 0; --i) {
        if (campaign.creatives[i].id == creativeID) {
          creative = campaign.creatives[i];
          break;
        }
      }
      const fileName = file.relativeName || file.name;
      const asset = {
        filename: file.fullPath || file.name || fileName,
        name: fileName,
        path: file.fullPath || file.name || fileName,
        new: true,
        creativeID: creativeID,
        url: ''
      };

      let formData = new FormData();

      const meta = analyzeFile(file);

      if (file.uploadType) {
        formData.append('type', file.uploadType);
      }
      if (file.width) {
        formData.append('width', file.width);
      }
      if (file.height) {
        formData.append('height', file.height);
      }
      if (file.duration) {
        formData.append('duration', file.duration);
      }

      if (meta) {
        for (let k in meta) {
          formData.append(k, meta[k]);
          asset[k] = meta[k];
        }
      }

      formData.append('creativeID', creativeID);
      formData.append('assets', JSON.stringify(asset));
      formData.append('files[]', file);
      axios
        .post(API_URL + '/v1/campaign/creatives/assets', formData, {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        .then(response => {
          if (response.data.status >= 200 && response.data.status < 400) {
            const asset = response.data.data[0];
            const deleted = response.data.deleted;
            let found = false;
            if (!creative) return;
            if (!creative.assets) creative.assets = [];
            for (let i = creative.assets.length - 1; i >= 0; --i) {
              if (creative.assets[i].filename == asset.filename) {
                creative.assets[i] = Object.assign(creative.assets[i], asset);
                found = true;
                break;
              }
            }
            if (!found) {
              creative.assets.push(asset);
            }
            creative.updatedAt = Date.now();
            if (deleted) {
              for (let m = 0; m < deleted.length; m++) {
                const delObj = deleted[m];
                for (let i = creative.assets.length - 1; i >= 0; --i) {
                  if (creative.assets[i].id == delObj.id) {
                    creative.assets.splice(i, 1);
                    break;
                  }
                }
              }
            }
            //state.saveState();
            return resolve(asset);
          }
          reject(response.data);
        });
      if (creative) {
        if (!creative.assets) creative.assets = [];
        let found = false;
        for (let i = creative.assets.length - 1; i >= 0; --i) {
          if (creative.assets[i].filename == asset.filename) {
            creative.assets[i] = Object.assign(creative.assets[i], asset);
            found = true;
            break;
          }
        }
        if (!found) {
          creative.assets.push(asset);
        }
      }
    })
  },


  deleteAsset: (store, state) => async (asset) => {
    if (!asset.creativeID) {
      console.error("NO creative ID")
      return false;
    }
    let creative = state.getCreativeByID(asset.creativeID);
    if (!creative) {

      console.error("Creative not found")
      return false;
    }
    return new Promise((resolve) => {

      axios
        .delete(
          API_URL + '/v1/campaign/creatives/assets?id=' + asset.id + '&creativeID=' + asset.creativeID,
          { withCredentials: true }
        )
        .then(response => {
          if (response.data.status >= 200 && response.data.status < 400) {
            if (!creative) return;
            if (!creative.assets) creative.assets = [];
            let useCreative = null;
            if (response.data.data) {

              let campaign = state.getByID(creative.campaignID);
              campaign.creatives.forEach((crtive) => {
                if (crtive.id == asset.creativeID) {
                  useCreative = crtive;
                }
              })
              useCreative.updatedAt = Date.now();
              const deletedAsset = response.data.data;
              for (let i = useCreative.assets.length - 1; i >= 0; i--) {
                if (useCreative.assets[i].id == deletedAsset.id || useCreative.assets[i].filename == deletedAsset.filename) {
                  useCreative.assets.splice(i, 1);
                  break;
                }
              }
              return resolve(deletedAsset);
            }
            state.saveState();
            resolve(asset);
          }
        });
    })
  },

  delete: (store, state) => (data) => {
    if (!data) return false;
    let findKey = data.id || data;

    for (let i = store.campaigns.length - 1; i >= 0; --i) {
      if (store.campaigns[i].id == findKey) {
        store.campaigns.splice(i, 1);
      }
    }
    state.saveState();

    return new Promise((resolve, reject) => {
      axios
        .delete(
          API_URL + '/v1/campaign/delete?campaignID=' + findKey,
          { withCredentials: true }
        )
        .then(response => {
          if (response.status === 200) {
            for (let i = store.campaigns.length - 1; i >= 0; --i) {
              if (store.campaigns[i].id == findKey) {
                store.campaigns.splice(i, 1);
                break;
              }
            }
            resolve(response.data);
          } else {
            reject(response);
          }
        });
    })

  },

  saveState: (store) => () => {
    try {
      const snapshot = JSON.parse(JSON.stringify(store));
      void setCampaignsState(snapshot).catch((err) => {
        console.error('Error saving campaigns state', err);
      });
    } catch (err) {
      console.error('Error saving campaigns state', err);
    }
  },

  // Line Items API (multi-size creative grouping)
  getLineItems: (store, state) => (campaignID) => {
    return new Promise((resolve, reject) => {
      axios
        .get(API_URL + '/v1/campaign/' + campaignID + '/line-items', { withCredentials: true })
        .then(response => {
          if (response.status >= 200 && response.status < 400) {
            const lineItems = response.data.data || response.data || [];
            state.saveState();
            resolve(lineItems);
          } else {
            reject(response);
          }
        })
        .catch((error) => reject(error.response || error));
    });
  },

  createLineItem: (store, state) => (campaignID, data) => {
    return new Promise((resolve, reject) => {
      axios
        .post(API_URL + '/v1/campaign/' + campaignID + '/line-items', data, {
          withCredentials: true
        })
        .then(response => {
          if (response.status >= 200 && response.status < 400) {
            const lineItem = response.data.data || response.data;
            state.saveState();
            resolve(lineItem);
          } else {
            reject(response);
          }
        })
        .catch((error) => reject(error.response || error));
    });
  },

  updateLineItem: (store, state) => (lineItemID, data) => {
    return new Promise((resolve, reject) => {
      axios
        .put(API_URL + '/v1/campaign/line-items/' + lineItemID, data, {
          withCredentials: true
        })
        .then(response => {
          if (response.status >= 200 && response.status < 400) {
            const lineItem = response.data.data || response.data;
            const campaign = state.getByID(lineItem.campaignID);
            if (campaign && campaign.lineItems && Array.isArray(campaign.lineItems)) {
              const existing = campaign.lineItems.find(li => li.id === lineItem.id);
              if (existing) {
                Object.assign(existing, lineItem);
              }
            }
            state.saveState();
            resolve(lineItem);
          } else {
            reject(response);
          }
        })
        .catch((error) => reject(error.response || error));
    });
  },

  /**
   * Get a setting value from a line item by key.
   * Line item settings use the same key-value structure as CreativeSettings.
   * @param {Object} lineItem - Line item object with settings array
   * @param {string} key - Setting key
   * @returns {*} The setting value or null
   */
  getLineItemSetting: () => (lineItem, key) => {
    if (!lineItem?.settings || !Array.isArray(lineItem.settings)) return null;
    const found = lineItem.settings.find(s => s.key === key);
    return found ? (found.value !== undefined ? found.value : found.getValue?.()) : null;
  },

  /**
   * Update a line item setting. Sends { settings: [{ key, value }] } to the API.
   * @param {Object} storeOrState - Vuex state (has campaigns array)
   * @param {Object} state - Service state with updateLineItem
   */
  updateLineItemSetting: (storeOrState, state) => (lineItemID, key, value) => {
    const campaigns = storeOrState?.campaigns || [];
    let lineItem = null;
    for (const c of campaigns) {
      lineItem = c.lineItems?.find(li => li.id === lineItemID);
      if (lineItem) break;
    }
    if (!lineItem) return Promise.reject(new Error('Line item not found'));
    if (!lineItem.settings) lineItem.settings = [];
    let found = false;
    for (const s of lineItem.settings) {
      if (s.key === key) { found = true; s.value = value; break; }
    }
    if (!found) lineItem.settings.push({ key, value });
    const settingsPayload = lineItem.settings.map(s => ({ key: s.key, value: s.value }));
    return axios.put(API_URL + '/v1/campaign/line-items/' + lineItemID, { settings: settingsPayload }, { withCredentials: true })
      .then(response => {
        if (response.status >= 200 && response.status < 400) {
          const updated = response.data.data || response.data;
          const campaign = state.getByID(updated.campaignID);
          if (campaign?.lineItems) {
            const existing = campaign.lineItems.find(li => li.id === updated.id);
            if (existing) Object.assign(existing, updated);
          }
          state.saveState();
          return updated;
        }
        return Promise.reject(response);
      })
      .catch(err => Promise.reject(err.response || err));
  },

  deleteLineItem: (store, state) => (lineItemID) => {
    return new Promise((resolve, reject) => {
      axios
        .delete(API_URL + '/v1/campaign/line-items/' + lineItemID, {
          withCredentials: true
        })
        .then(response => {
          if (response.status >= 200 && response.status < 400) {
            state.saveState();
            resolve(response.data);
          } else {
            reject(response);
          }
        })
        .catch((error) => reject(error.response || error));
    });
  },

  /**
   * Update all creatives under a line item in one API call.
   * Used when editing a Creative Set - shared options apply to all sizes.
   * @param {string} lineItemID - The line item (Creative Set) ID
   * @param {Object} updates - Fields to apply to all creatives. See docs/line-item-creatives-update-api.md
   */
  updateLineItemCreatives: (store, state) => (lineItemID, updates) => {
    return new Promise((resolve, reject) => {
      axios
        .put(API_URL + '/v1/campaign/line-items/' + lineItemID + '/creatives', updates, {
          withCredentials: true
        })
        .then(response => {
          if (response.status >= 200 && response.status < 400) {
            const data = response.data.data || response.data;
            const campaignID = data.campaignID || (data.creatives && data.creatives[0] && data.creatives[0].campaignID);
            const campaign = campaignID ? state.getByID(campaignID) : null;
            if (campaign && data.creatives && Array.isArray(data.creatives)) {
              data.creatives.forEach((updatedCreative) => {
                const existing = campaign.creatives.find(c => c.id === updatedCreative.id);
                if (existing) {
                  Object.assign(existing, updatedCreative);
                }
              });
            }
            state.saveState();
            resolve(data);
          } else {
            reject(response);
          }
        })
        .catch((error) => reject(error.response || error));
    });
  },

}

const CampaignsServicePublicFunctions = [
  'getByID',
  'getCreativeByID',
  'getAll',
  'getCreatives',
  'createNew',
  'createNewCreative',
  'updateCreative',
  'duplicateCreative',
  'duplicate',
  'update',
  'delete',
  'saveState',
  'getCreativeTags',
  'uploadAsset',
  'deleteAsset',
  'publishCreative',
  'deleteCreative',
  'getCampaign',
  'getCampaignReport',
  'getCreativesReport',
  'getCampaignDateReport',
  'getCreativesDateReport',
  'getCreativesDateEvents',
  'createNewReportToken',
  'getSharedDateReport',
  'createNewScheduledReport',
  'deleteScheduledReport',
  'downloadTags',
  'getCreativeSetting',
  'updateCreativeHTML',
  'getCreativeHTMLAsset',
  'optimizeAssets',
  // Line Items
  'getLineItems',
  'createLineItem',
  'updateLineItem',
  'deleteLineItem',
  'updateLineItemCreatives',
  'getLineItemSetting',
  'updateLineItemSetting'
];

export { CampaignsService, CampaignsServicePublicFunctions };
